import { useEffect, useRef, useState } from "react";
import { motion, AnimatePresence, useScroll, useTransform } from "framer-motion";
import { scrollToSection } from "@/components/Navbar";

const SLIDES = [
  { src: "/images/hero-aerial.jpg", alt: "Aerial view of a completed multifamily community — team experience by Veritas Development Group", label: "Multifamily — Completed Community" },
  { src: "/images/hero-ridgeview.jpg", alt: "Completed multifamily community with mountain backdrop — team experience by Veritas Development Group", label: "Multifamily — Completed Community" },
  { src: "/images/hero-northcourt.jpg", alt: "Completed modern multifamily building — team experience by Veritas Development Group", label: "Multifamily — Completed Community" },
  { src: "/images/hero-dusk.jpg", alt: "Completed multifamily building at dusk with warm interior lighting — team experience by Veritas Development Group", label: "Multifamily — Completed Community" },
];

const MaskedLine = ({ children, delay, className }) => (
  <span className="block overflow-hidden pb-1">
    <motion.span
      initial={{ y: "115%" }}
      animate={{ y: 0 }}
      transition={{ duration: 1.1, delay, ease: [0.16, 1, 0.3, 1] }}
      className={`block ${className}`}
    >
      {children}
    </motion.span>
  </span>
);

export default function Hero() {
  const ref = useRef(null);
  const [slide, setSlide] = useState(0);
  const { scrollYProgress } = useScroll({
    target: ref,
    offset: ["start start", "end start"],
  });
  const bgY = useTransform(scrollYProgress, [0, 1], ["0%", "22%"]);
  const fade = useTransform(scrollYProgress, [0, 0.75], [1, 0]);

  useEffect(() => {
    const t = setInterval(() => setSlide((s) => (s + 1) % SLIDES.length), 6000);
    return () => clearInterval(t);
  }, []);

  return (
    <section ref={ref} id="top" className="relative min-h-[100svh] overflow-hidden" data-testid="hero-section">
      <motion.div style={{ y: bgY }} className="absolute inset-0">
        <AnimatePresence mode="sync">
          <motion.img
            key={slide}
            src={SLIDES[slide].src}
            alt={SLIDES[slide].alt}
            initial={{ opacity: 0, scale: 1.04 }}
            animate={{ opacity: 1, scale: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 1.4, ease: [0.16, 1, 0.3, 1] }}
            className="absolute inset-0 w-full h-full object-cover"
            data-testid="hero-background-image"
          />
        </AnimatePresence>
      </motion.div>
      <div className="absolute inset-0 bg-black/45" />
      <div className="absolute inset-0 bg-gradient-to-t from-black/90 via-black/30 to-black/40" />
      <div className="absolute inset-0 bg-gradient-to-r from-black/55 via-black/10 to-transparent" />
      <div className="absolute inset-x-0 bottom-0 h-40 bg-[#050505]/80 blur-2xl" />

      <motion.div
        style={{ opacity: fade }}
        className="relative z-10 min-h-[100svh] max-w-7xl mx-auto px-6 lg:px-10 flex flex-col justify-end pt-36 pb-24 sm:pb-28"
      >
        <motion.p
          initial={{ opacity: 0, y: 12 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.9, delay: 0.5 }}
          className="text-[11px] sm:text-xs tracking-[0.45em] uppercase text-[#D4AF37] mb-6"
          data-testid="hero-overline"
        >
          Veritas Development Group LLC
        </motion.p>

        <h1 className="font-display font-light leading-[0.95] tracking-tight" data-testid="hero-headline">
          <MaskedLine delay={0.65} className="text-4xl sm:text-7xl lg:text-8xl text-[#F5F5F5]">
            Built on Integrity.
          </MaskedLine>
          <MaskedLine delay={0.85} className="text-4xl sm:text-7xl lg:text-8xl italic text-[#D4AF37]">
            Driven by Excellence.
          </MaskedLine>
        </h1>

        <motion.p
          initial={{ opacity: 0, y: 14 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.9, delay: 1.1 }}
          className="mt-7 text-[12px] sm:text-sm tracking-[0.22em] uppercase text-[#F5F5F5]"
          data-testid="hero-disciplines"
        >
          Real Estate Development <span className="text-[#D4AF37]">•</span> Commercial Construction <span className="text-[#D4AF37]">•</span> Construction Management
        </motion.p>

        <motion.p
          initial={{ opacity: 0, y: 12 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.9, delay: 1.2 }}
          className="mt-4 text-[10px] sm:text-[11px] tracking-[0.28em] uppercase text-[#A3A3A3]"
          data-testid="hero-trades"
        >
          Site Development <span className="text-[#D4AF37]/70">•</span> Concrete <span className="text-[#D4AF37]/70">•</span> Excavation <span className="text-[#D4AF37]/70">•</span> Residential Construction
        </motion.p>

        <motion.p
          initial={{ opacity: 0, y: 16 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.9, delay: 1.35 }}
          className="mt-5 max-w-xl text-sm sm:text-base font-light leading-relaxed tracking-wide text-[#A3A3A3]"
          data-testid="hero-subtext"
        >
          Serving Lee's Summit & Greater Kansas City — Residential | Commercial | Development.
        </motion.p>

        <motion.div
          initial={{ opacity: 0, y: 16 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.9, delay: 1.45 }}
          className="mt-10 flex flex-wrap items-center gap-5"
        >
          <button
            data-testid="hero-consultation-button"
            onClick={() => scrollToSection("#contact")}
            className="bg-[#D4AF37] text-black px-8 py-4 text-[11px] tracking-[0.3em] uppercase font-medium hover:bg-[#F3E5AB] transition-colors duration-300"
          >
            Request a Project Estimate
          </button>
          <button
            data-testid="hero-services-button"
            onClick={() => scrollToSection("#services")}
            className="border border-white/25 text-[#F5F5F5] px-8 py-4 text-[11px] tracking-[0.3em] uppercase hover:border-[#D4AF37] hover:text-[#D4AF37] transition-colors duration-300"
          >
            Explore Services
          </button>
        </motion.div>
      </motion.div>

      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ duration: 1.2, delay: 1.8 }}
        className="absolute bottom-10 right-8 lg:right-14 z-10 hidden md:block"
        data-testid="hero-badge"
      >
        <div className="relative w-28 h-28">
          <svg viewBox="0 0 120 120" className="w-28 h-28 animate-spin-slower">
            <defs>
              <path id="badge-circle" d="M60,60 m-46,0 a46,46 0 1,1 92,0 a46,46 0 1,1 -92,0" />
            </defs>
            <text fill="#D4AF37" fontSize="9.5" letterSpacing="3.5" fontFamily="Outfit, sans-serif">
              <textPath href="#badge-circle">LEE'S SUMMIT, MO • VERITAS • BUILT ON INTEGRITY •</textPath>
            </text>
          </svg>
          <span className="absolute inset-0 flex items-center justify-center">
            <span className="w-2.5 h-2.5 rotate-45 border border-[#D4AF37]" />
          </span>
        </div>
      </motion.div>

      <motion.div
        initial={{ opacity: 0, y: 12 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 1, delay: 1.9 }}
        className="absolute bottom-8 left-1/2 -translate-x-1/2 z-10 flex flex-col items-center gap-3"
        data-testid="hero-slideshow-indicator"
      >
        <span className="text-[9px] tracking-[0.4em] uppercase text-[#F5F5F5]/80" data-testid="hero-slide-label">
          {SLIDES[slide].label}
        </span>
        <div className="flex items-center gap-2.5">
          {SLIDES.map((s, i) => (
            <button
              key={s.src}
              data-testid={`hero-slide-dot-${i}`}
              onClick={() => setSlide(i)}
              aria-label={`Show photo ${i + 1}`}
              className={`h-[3px] transition-all duration-500 ${
                i === slide ? "w-10 bg-[#D4AF37]" : "w-4 bg-white/40 hover:bg-white/70"
              }`}
            />
          ))}
        </div>
      </motion.div>

      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ delay: 2, duration: 1 }}
        className="absolute bottom-0 left-6 lg:left-10 z-10 hidden sm:flex flex-col items-center gap-3"
      >
        <span className="text-[9px] tracking-[0.4em] uppercase text-[#A3A3A3] [writing-mode:vertical-lr]">Scroll</span>
        <span className="w-px h-16 bg-[#D4AF37]/70 scroll-cue-line" />
      </motion.div>
    </section>
  );
}
